// @flow

// Effect Manager
import { fork, put, select, take } from 'redux-saga/effects'
import _find from 'lodash/find'
import _forEach from 'lodash/forEach'
import _map from 'lodash/map'

// Utilities
import traverse from 'fc-utils/traverse'

// Action Types
import {
  ADD_SOILJOB,
  EDIT_SOILJOB,
  ADD_TABLE_SOILJOB,
  EDIT_TABLE_SOILJOB,
  DELETE_TABLE_SOILJOB,
} from '../constants'

// Selectors
import gridOffset$ from 'hfty-map/src/selectors/gridOffset'
import { featuresOfSelectedField$ } from 'hfty-map'
import { currentUserUnits$ } from 'hfty-users'
import { jobs$ } from 'hfty-soil-jobs'

import datatable from '../datatable'

// Operator
import soilJobsOperator from 'fc-operator-sampling-jobs'
import tableOperator from '../tableOperator'

// Constants
import { PROP_US_CUSTOMARY } from 'fc-model-users'
import { GRID_UNIT_ACRES, GRID_UNIT_HECTARES } from 'hfty-model-grid'
import {
  GRID_TYPE,
  SET_GRID,
  READY_TO_SAMPLE,
  TYPE_AGPHD_SOILS,
} from 'fc-model-sampling-jobs'
import { DEFAULT_GRID_TRANSFORMATIONS } from 'hfty-grid/src/constants'

// Exports
export default function *watchSoilJobOperations (): Generator<*, void, *> {
  while (true) {
    const { type, payload } = yield take([ADD_SOILJOB, EDIT_SOILJOB,
      ADD_TABLE_SOILJOB, EDIT_TABLE_SOILJOB, DELETE_TABLE_SOILJOB])
    if (type === ADD_SOILJOB) {
      yield fork(handleAddSoilJob, payload)
    } else if (type === EDIT_SOILJOB) {
      yield fork(handleEditSoilJobs, payload)
    } else if (type === ADD_TABLE_SOILJOB) {
      yield fork(handleAddTableSoilJob, payload)
    } else if (type === EDIT_TABLE_SOILJOB) {
      yield fork(handleEditTableSoilJob, payload)
    } else {
      yield fork(handleDeleteTableSoilJob, payload)
    }
  }
}

export function *handleAddSoilJob (payload: Object): Generator<*, void, *> {
  // $FlowFixMe
  const gridOffset = yield select(gridOffset$)
  const features = yield select(featuresOfSelectedField$)
  const units = yield select(currentUserUnits$)

  const transformations = {
    size: 5,
    rotation: 0,
    offset: gridOffset,
  }

  let gridData = {}

  _forEach(features, ({properties}: object) => {
    gridData[properties.featureIndex] = transformations
  })
  const operation = soilJobsOperator.create({
    model: {
      job: {
        created_by: payload.userId,
        subfield: payload.subfieldId,
        samplingType: GRID_TYPE,
        status: READY_TO_SAMPLE,
        assigned_to: null,
        type: TYPE_AGPHD_SOILS,
      },
      gridData: {
        jobId: null,
        grid: {
          ...gridData,
          transformations,
          units: units === PROP_US_CUSTOMARY ? GRID_UNIT_ACRES : GRID_UNIT_HECTARES,
        },
      },
    },
  })
  yield put(soilJobsOperator.actionCreators.add(operation))
}

export function *handleEditSoilJobs (payload: Object): Generator<*, void, *> {
  const gridFeatures = traverse(['grid', 'features'])(payload)
  const fieldFeatures = yield select(featuresOfSelectedField$)
  let featureData = {}
  let firstFeatureTransformationData = null

  const units = _find(gridFeatures, (feature: Object): string => traverse(['properties', 'units'])(feature))
  _forEach(fieldFeatures, ({properties}: Object) => {
    const matchingFeature = _find(gridFeatures, (feature: Object): boolean => (
      feature.properties.featureIndex === properties.featureIndex))
    let data = {size: 5, offset: null, rotation: 0}
    if (matchingFeature) {
      const oldData = {
        size: matchingFeature.properties.size,
        offset: matchingFeature.properties.offset,
        rotation: matchingFeature.properties.rotation,
      }
      data = oldData || matchingFeature.properties.transformationData
      if (!firstFeatureTransformationData) {
        firstFeatureTransformationData = data
      }
    }
    featureData[properties.featureIndex] = data
  })

  const transformations = firstFeatureTransformationData || DEFAULT_GRID_TRANSFORMATIONS

  const operation = soilJobsOperator.create({
    model: {
      job: payload,
      gridData: {
        jobId: payload.id,
        grid: {
          ...featureData,
          transformations,
          units: traverse(['properties', 'units'])(units) || 'acres',
        },
      },
    },
  })

  yield put(soilJobsOperator.actionCreators.edit(operation))
}

export function *handleAddTableSoilJob (payload: Object): Generator<*, void, *> {
  const operation = tableOperator.create({
    model: {
      created_by: payload.userId,
      subfield: payload.subfieldId,
      samplingType: GRID_TYPE,
      status: SET_GRID,
      assigned_to: null,
      type: TYPE_AGPHD_SOILS,
    },
  })
  yield put(tableOperator.actionCreators.add(operation))
}

export function *handleEditTableSoilJob (payload: Object): Generator<*, void, *> {
  const jobs: Job = yield select(jobs$)
  const selectedIds: Array<number> = yield select(datatable.selectors.selectedRecordIds$)

  const operations = _map(selectedIds, function (jobId: number): Object {
    let job = _find(jobs, {id: jobId})
    return tableOperator.create({
      model: job,
    })
  })

  yield put(tableOperator.actionCreators.edit(...operations))
}

export function *handleDeleteTableSoilJob (payload: Object): Generator<*, void, *> {
  const jobs: Job = yield select(jobs$)
  const selectedIds: Array<number> = yield select(datatable.selectors.selectedRecordIds$)
  const operations = selectedIds.map(function (jobId: number): * {
    const job: Job = _find(jobs, {id: jobId})
    return tableOperator.create({
      model: job,
    })
  })
  yield put(tableOperator.actionCreators.delete(...operations))
}
